<script setup>
import { computed, defineProps, getCurrentInstance } from 'vue'
import { useStore } from '@/utils/VuexHelpers'

const props = defineProps({
  customerId: {
    type: Number,
    default: null
  },
  display: {
    type: String,
    default: 'code-name', // options: code, name, code-name
    validator: (value) => ['code', 'name', 'code-name'].includes(value)
  },
  showInactive: {
    type: Boolean,
    default: true
  },
  fallback: {
    type: String,
    default: '-'
  },
  maxNameLength: {
    type: Number,
    default: null
  }
})

const vm = getCurrentInstance().proxy
const store = useStore()

const customerNames = computed(() => store.getters.customerNames || [])
const loadingCustomers = computed(() => store.getters.loadingCustomers)

const customer = computed(() => {
  if (!props.customerId) return null

  return customerNames.value.find((item) => {
    return Number(item.id) === Number(props.customerId)
  })
})

const truncateText = (value, maxLength) => {
  if (!value) return ''

  if (!maxLength || value.length <= maxLength) {
    return value
  }

  return `${value.slice(0, maxLength)}...`
}

const customerLabel = computed(() => {
  if (!props.customerId) return props.fallback

  if (loadingCustomers.value === 'names' && !customer.value) {
    return 'Loading...'
  }

  if (!customer.value) return props.fallback

  const code = customer.value.code || ''
  const name = truncateText(customer.value.name || '', props.maxNameLength)

  if (props.display === 'code') return code || props.fallback
  if (props.display === 'name') return name || props.fallback

  if (code && name) return `${code} - ${name}`

  return code || name || props.fallback
})

const fullCustomerLabel = computed(() => {
  if (!customer.value) return props.fallback

  const code = customer.value.code || ''
  const name = customer.value.name || ''

  if (props.display === 'code') return code || props.fallback
  if (props.display === 'name') return name || props.fallback

  if (code && name) return `${code} - ${name}`

  return code || name || props.fallback
})

if (customerNames.value.length === 0 && loadingCustomers.value !== 'names') {
  store.dispatch('fetchCustomerNames', { vm })
}
</script>

<template>
  <span :title="fullCustomerLabel">
    {{ customerLabel }}

    <span
      v-if="showInactive && customer && customer.is_active !== true"
      class="red--text text-caption ml-1"
    >
      Inactive
    </span>
  </span>
</template>
